import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Loader2, FileQuestion, Gauge, AlertTriangle, CheckCircle2, MessageSquareWarning, Scale } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const STRENGTH = {
  strong: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  partial: 'bg-amber-50 text-amber-700 border-amber-200',
  none: 'bg-rose-50 text-rose-700 border-rose-200',
};
const LVL = {
  high: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  med: 'bg-amber-50 text-amber-700 border-amber-200',
  low: 'bg-rose-50 text-rose-700 border-rose-200',
};

export default function RfpSharePage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [name, setName] = useState('');
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);

  useEffect(() => {
    axios.get(`${API_URL}/api/rfp/response/shared/${token}`)
      .then(res => setData(res.data))
      .catch(err => setError(err?.response?.data?.detail || 'This share link is invalid or has been revoked'));
  }, [token]);

  const signoff = async (decision) => {
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/rfp/response/shared/${token}/signoff`,
        { decision, name: name.trim(), comment: comment.trim() });
      setSubmitted(decision);
      setData(prev => ({
        ...prev,
        signoffs: [...(prev.signoffs || []), { decision, name: name.trim(), comment: comment.trim(), at: new Date().toISOString() }],
      }));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not record your decision');
    } finally { setSubmitting(false); }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <p className="text-sm text-slate-500" data-testid="rfp-share-error">{error}</p>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-[#4ABFCF]" />
      </div>
    );
  }
  const rs = data.response_score;
  return (
    <div className="min-h-screen bg-slate-50" data-testid="rfp-share-page">
      <header className="bg-[#0F2040] text-white px-6 py-4">
        <div className="max-w-5xl mx-auto">
          <p className="text-[10px] uppercase tracking-widest text-[#4ABFCF]">Seertech Blueprint · RFP Response — read-only stakeholder view</p>
          <h1 className="text-xl font-bold flex items-center gap-2 mt-1">
            <FileQuestion className="w-5 h-5 text-[#4ABFCF]" />{data.name}
          </h1>
          {data.client_name && <p className="text-xs text-slate-300">{data.client_name}</p>}
        </div>
      </header>
      <main className="max-w-5xl mx-auto p-6 space-y-5">
        {rs && (
          <div className="bg-white border border-[#4ABFCF]/40 rounded-md p-4 space-y-2" data-testid="rfp-share-score">
            <p className="text-xs font-semibold text-[#0F2040] flex items-center gap-1.5">
              <Gauge className="w-4 h-4 text-[#4ABFCF]" />Response Score
            </p>
            <div className="flex items-center gap-6 flex-wrap">
              <div><span className="text-3xl font-bold text-[#0F2040]">{rs.overall.confidence_score}%</span>
                <span className="text-[10px] text-slate-500 ml-1.5">response confidence</span></div>
              <div><span className="text-3xl font-bold text-[#0F2040]">{rs.overall.capability_score}%</span>
                <span className="text-[10px] text-slate-500 ml-1.5">capability match</span></div>
            </div>
            {rs.narrative && <p className="text-xs text-slate-600 leading-relaxed">{rs.narrative}</p>}
          </div>
        )}
        {data.scoring_scheme && (() => {
          const qs = (data.documents || []).flatMap(d => d.questions || []);
          const nums = qs.map(q => parseFloat(q.client_score)).filter(n => !Number.isNaN(n));
          const byLevel = {};
          qs.forEach(q => { if (q.client_score) byLevel[q.client_score] = (byLevel[q.client_score] || 0) + 1; });
          if (!Object.keys(byLevel).length) return null;
          return (
            <div className="bg-white border border-[#4ABFCF]/40 rounded-md p-4 space-y-2" data-testid="rfp-share-client-scoring">
              <p className="text-xs font-semibold text-[#0F2040] flex items-center gap-1.5">
                <Scale className="w-4 h-4 text-[#4ABFCF]" />Client Scoring Fit
              </p>
              <div className="flex items-center gap-2 flex-wrap">
                {nums.length > 0 && (
                  <>
                    <span className="text-3xl font-bold text-[#0F2040]" data-testid="rfp-share-cscore-avg">
                      {(nums.reduce((s, n) => s + n, 0) / nums.length).toFixed(1)}
                    </span>
                    <span className="text-[10px] text-slate-500">average on the client's own scoring scale</span>
                  </>
                )}
                {data.scoring_scheme.levels.filter(lv => byLevel[lv.label]).map(lv => (
                  <Badge key={lv.label} variant="outline" className="text-[9px] bg-slate-50 text-slate-600 border-slate-200"
                    title={lv.definition} data-testid={`rfp-share-cscore-level-${lv.label.replace(/[^A-Za-z0-9]/g, '_')}`}>
                    {lv.label} × {byLevel[lv.label]}
                  </Badge>
                ))}
              </div>
            </div>
          );
        })()}
        {(data.documents || []).map(d => (
          <div key={d.filename} className="bg-white border border-slate-200 rounded-md p-4 space-y-3" data-testid={`rfp-share-doc-${d.filename}`}>
            <div className="flex items-center gap-3 flex-wrap">
              <span className="text-sm font-semibold text-[#0F2040]">{d.filename}</span>
              {d.scores && (
                <>
                  <span className="text-xl font-bold text-[#0F2040]">{d.scores.overall}%</span>
                  <span className="text-[10px] text-slate-500">match score</span>
                  <Badge variant="outline" className="text-[9px] bg-emerald-50 text-emerald-700 border-emerald-200">{d.scores.strong} strong</Badge>
                  <Badge variant="outline" className="text-[9px] bg-amber-50 text-amber-700 border-amber-200">{d.scores.partial} partial</Badge>
                  <Badge variant="outline" className="text-[9px] bg-rose-50 text-rose-700 border-rose-200">{d.scores.gaps} gaps</Badge>
                </>
              )}
            </div>
            {d.summary && (
              <p className="text-xs text-slate-600 bg-slate-50 border border-slate-100 rounded-md p-2.5 leading-relaxed">
                <AlertTriangle className="w-3 h-3 inline mr-1 text-amber-500 -mt-px" />{d.summary}
              </p>
            )}
            <div className="border border-slate-100 rounded-md divide-y divide-slate-50">
              {(d.questions || []).map(q => (
                <div key={q.idx} className="px-3 py-2.5 space-y-1.5" data-testid={`rfp-share-q-${q.idx}`}>
                  <div className="flex items-start gap-2">
                    <Badge variant="outline" className={`text-[10px] mt-0.5 shrink-0 ${q.is_pricing ? 'bg-sky-50 text-sky-700 border-sky-200' : STRENGTH[q.strength] || ''}`}>
                      {q.is_pricing ? 'Pricing' : q.strength === 'none' ? 'Gap' : q.strength}
                    </Badge>
                    <p className="text-xs font-medium text-[#0F2040]">{q.text}</p>
                  </div>
                  {q.response ? (
                    <div className="text-xs text-slate-600 leading-relaxed pl-1 max-w-none [&_p]:my-1 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:my-1 [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:my-1 [&_strong]:font-semibold [&_h1]:font-semibold [&_h2]:font-semibold [&_h3]:font-semibold">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{q.response}</ReactMarkdown>
                    </div>
                  ) : (
                    <p className="text-[11px] text-slate-400 italic pl-1">{q.is_pricing ? 'Commercial response to follow.' : 'Response in progress.'}</p>
                  )}
                  {(q.attachments || []).length > 0 && (
                    <div className="flex flex-wrap gap-2 pl-1" data-testid={`rfp-share-attach-${q.idx}`}>
                      {q.attachments.map((a, ai) => (a.content_type || '').startsWith('image/') ? (
                        <img key={ai} src={`${API_URL}/api/rfp/files/${a.path}`} alt={a.filename}
                          className="max-h-40 min-h-[48px] rounded border border-slate-200" />
                      ) : (
                        <span key={ai} className="text-[10px] text-slate-500 bg-slate-50 border border-slate-200 rounded-full px-2 py-0.5">{a.filename}</span>
                      ))}
                    </div>
                  )}
                  <div className="flex items-center gap-1.5 flex-wrap">
                    {q.client_score && (
                      <Badge variant="outline" className="text-[9px] bg-[#4ABFCF]/10 text-[#0F2040] border-[#4ABFCF]/40"
                        title={q.client_score_reviewed ? 'Confirmed by the response team' : 'AI-suggested, pending team review'}
                        data-testid={`rfp-share-cscore-${q.idx}`}>
                        Client score: {q.client_score}{q.client_score_reviewed ? ' ✓' : ''}
                      </Badge>
                    )}
                    {q.confidence && <Badge variant="outline" className={`text-[9px] ${LVL[q.confidence]}`}>Confidence: {q.confidence}</Badge>}
                    {q.fit && <Badge variant="outline" className={`text-[9px] ${LVL[q.fit]}`}>Fit: {q.fit}</Badge>}
                    {q.flag_label && <Badge variant="outline" className="text-[9px] text-slate-500">{q.flag_label}</Badge>}
                    {q.match?.capability_id && <span className="text-[9px] text-slate-400 font-mono">{q.match.capability_id}</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
        <div className="bg-white border border-slate-200 rounded-md p-4 space-y-3" data-testid="rfp-share-signoff">
          <p className="text-xs font-semibold text-[#0F2040]">Stakeholder sign-off</p>
          {(data.signoffs || []).length > 0 && (
            <div className="space-y-1" data-testid="rfp-share-signoff-list">
              {data.signoffs.map((s, i) => (
                <div key={i} className="flex items-start gap-2 text-xs">
                  <Badge variant="outline" className={`text-[9px] shrink-0 ${s.decision === 'approved' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}>
                    {s.decision === 'approved' ? 'Approved' : 'Changes requested'}
                  </Badge>
                  <span className="font-medium text-[#0F2040]">{s.name || 'Anonymous'}</span>
                  {s.comment && <span className="text-slate-500 min-w-0 flex-1">{s.comment}</span>}
                  <span className="text-[9px] text-slate-400 shrink-0">{s.at ? new Date(s.at).toLocaleDateString() : ''}</span>
                </div>
              ))}
            </div>
          )}
          {submitted ? (
            <p className="text-sm text-emerald-700 flex items-center gap-1.5" data-testid="rfp-share-signoff-thanks">
              <CheckCircle2 className="w-4 h-4" />Thank you — your decision ({submitted === 'approved' ? 'Approved' : 'Changes requested'}) has been recorded and the team has been notified.
            </p>
          ) : (
            <div className="space-y-2">
              <Input value={name} onChange={e => setName(e.target.value.slice(0, 80))} placeholder="Your name"
                className="h-8 text-xs max-w-xs" data-testid="rfp-share-signoff-name" />
              <textarea value={comment} onChange={e => setComment(e.target.value.slice(0, 1000))} rows={2}
                placeholder="Optional comment (required context if requesting changes)…"
                className="w-full text-xs border border-slate-200 rounded-md p-2 text-slate-700 focus:outline-none focus:border-[#4ABFCF] resize-y"
                data-testid="rfp-share-signoff-comment" />
              <div className="flex gap-2">
                <Button size="sm" disabled={submitting} onClick={() => signoff('approved')}
                  className="h-8 text-xs bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="rfp-share-approve-btn">
                  {submitting ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5 mr-1" />}Approve
                </Button>
                <Button size="sm" variant="outline" disabled={submitting} onClick={() => signoff('changes_requested')}
                  className="h-8 text-xs text-amber-700 border-amber-300 hover:bg-amber-50" data-testid="rfp-share-changes-btn">
                  <MessageSquareWarning className="w-3.5 h-3.5 mr-1" />Request changes
                </Button>
              </div>
            </div>
          )}
        </div>
        <p className="text-[10px] text-slate-400 text-center pb-6">COMMERCIAL IN CONFIDENCE — Seertech Solutions · Read-only view{data.expires_at ? ` · Link expires ${new Date(data.expires_at).toLocaleDateString()}` : ''}</p>
      </main>
    </div>
  );
}
